import { useState, useCallback } from 'react';
import * as dbOps from '../db/kioskDbOperations';

// ============================================================
// Helpers
// ============================================================

function escapeCSV(value) {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadFile(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatHours(ms) {
  if (!ms || ms <= 0) return '';
  return (ms / 3600000).toFixed(2);
}

function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Pair raw clock events into structured shift records.
 * Returns array of shift objects with clock_in, clock_out, breaks, duration.
 */
function pairEventsIntoShifts(events, employees, breakTypes) {
  // Build lookup maps
  const empLookup = {};
  for (const emp of employees) {
    empLookup[emp.id] = emp.displayName || `${emp.firstName} ${emp.lastName}`.trim() || 'Unknown';
  }
  const breakLookup = {};
  for (const bt of breakTypes) {
    breakLookup[bt.id] = bt.name;
  }

  // Group events by userId
  const byUser = {};
  for (const event of events) {
    if (!byUser[event.userId]) byUser[event.userId] = [];
    byUser[event.userId].push(event);
  }

  const shifts = [];

  for (const [userId, userEvents] of Object.entries(byUser)) {
    userEvents.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    let currentShift = null;
    let currentBreak = null;

    for (const event of userEvents) {
      if (event.action === 'clock_in') {
        // Start new shift
        currentShift = {
          userId,
          employeeName: empLookup[userId] || userId,
          clockIn: event.timestamp,
          clockOut: null,
          method: event.method || '',
          syncStatus: event.syncStatus,
          breaks: [],
        };
        currentBreak = null;
      } else if (event.action === 'clock_out' && currentShift) {
        currentShift.clockOut = event.timestamp;
        if (event.syncStatus !== 'synced') {
          currentShift.syncStatus = event.syncStatus;
        }
        // Close any open break
        if (currentBreak) {
          currentBreak.endTime = event.timestamp;
          currentBreak = null;
        }
        shifts.push(currentShift);
        currentShift = null;
      } else if (event.action === 'break_start' && currentShift) {
        currentBreak = {
          startTime: event.timestamp,
          endTime: null,
          breakType: breakLookup[event.breakTypeId] || 'Break',
        };
        currentShift.breaks.push(currentBreak);
      } else if (event.action === 'break_end' && currentBreak) {
        currentBreak.endTime = event.timestamp;
        currentBreak = null;
      }
    }

    // If there's an open shift (no clock out), still include it
    if (currentShift) {
      shifts.push(currentShift);
    }
  }

  // Sort by clock-in time
  shifts.sort((a, b) => a.clockIn.localeCompare(b.clockIn));
  return shifts;
}

// ============================================================
// Hook
// ============================================================

export function useKioskExport() {
  const [exporting, setExporting] = useState(false);
  const [qrData, setQrData] = useState(null); // { chunks: string[], totalChunks: number, meta: {} }

  // -----------------------------------------------------------------------
  // Export to CSV — paired shift format
  // -----------------------------------------------------------------------
  const exportToCSV = useCallback(async (startDate, endDate) => {
    setExporting(true);
    try {
      const [events, employees, breakTypes, deviceInfo] = await Promise.all([
        dbOps.getEventsByDateRange(startDate, endDate + 'T23:59:59'),
        dbOps.getAllEmployees(),
        dbOps.getAllBreakTypes(),
        dbOps.getConfig('device_info'),
      ]);

      const shifts = pairEventsIntoShifts(events, employees, breakTypes);
      const siteName = deviceInfo?.location_name || 'kiosk';

      // Build CSV — one row per shift, with break columns expanded
      const headers = [
        'Employee Name',
        'Employee ID',
        'Date',
        'Clock In',
        'Clock Out',
        'Shift Duration (hrs)',
        'Break Count',
        'Total Break Duration (hrs)',
        'Break Details',
        'Method',
        'Sync Status',
      ];

      const rows = shifts.map((shift) => {
        const clockInMs = new Date(shift.clockIn).getTime();
        const clockOutMs = shift.clockOut ? new Date(shift.clockOut).getTime() : null;
        const shiftDurationMs = clockOutMs ? clockOutMs - clockInMs : null;

        let totalBreakMs = 0;
        const breakDetails = shift.breaks
          .map((b) => {
            const bStart = new Date(b.startTime).getTime();
            const bEnd = b.endTime ? new Date(b.endTime).getTime() : Date.now();
            totalBreakMs += bEnd - bStart;
            return `${b.breakType}: ${formatTime(b.startTime)}-${b.endTime ? formatTime(b.endTime) : 'ongoing'}`;
          })
          .join('; ');

        return [
          shift.employeeName,
          shift.userId,
          formatDate(shift.clockIn),
          shift.clockIn,
          shift.clockOut || '',
          formatHours(shiftDurationMs),
          shift.breaks.length,
          formatHours(totalBreakMs),
          breakDetails,
          shift.method,
          shift.syncStatus,
        ];
      });

      const csvContent = [
        headers.map(escapeCSV).join(','),
        ...rows.map((row) => row.map(escapeCSV).join(',')),
      ].join('\n');

      const safeSite = siteName.replace(/[^a-zA-Z0-9-_]/g, '_');
      downloadFile(
        csvContent,
        `kiosk_export_${safeSite}_${startDate}_to_${endDate}.csv`,
        'text/csv;charset=utf-8;'
      );

      return { success: true, shiftCount: shifts.length, eventCount: events.length };
    } catch (err) {
      console.error('CSV export failed:', err);
      return { success: false, error: err.message };
    } finally {
      setExporting(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Export to JSON — full data for server-side import
  // -----------------------------------------------------------------------
  const exportToJSON = useCallback(async (startDate, endDate) => {
    setExporting(true);
    try {
      const [events, employees, breakTypes, deviceInfo, config] = await Promise.all([
        dbOps.getEventsByDateRange(startDate, endDate + 'T23:59:59'),
        dbOps.getAllEmployees(),
        dbOps.getAllBreakTypes(),
        dbOps.getConfig('device_info'),
        dbOps.getAllConfig(),
      ]);

      const exportData = {
        version: 1,
        exportedAt: new Date().toISOString(),
        device: {
          id: deviceInfo?.id || null,
          name: deviceInfo?.device_name || null,
          location: deviceInfo?.location_name || null,
          companyId: deviceInfo?.company_id || null,
          projectId: deviceInfo?.project_id || null,
          siteLocationId: deviceInfo?.site_location_id || null,
        },
        dateRange: { start: startDate, end: endDate },
        events: events.map((e) => ({
          tempId: e.tempId,
          userId: e.userId,
          action: e.action,
          timestamp: e.timestamp,
          method: e.method,
          hasSelfie: e.hasSelfie,
          breakTypeId: e.breakTypeId,
          syncStatus: e.syncStatus,
          serverCheckinId: e.serverCheckinId,
        })),
        employees: employees.map((e) => ({
          id: e.id,
          displayName: e.displayName,
          firstName: e.firstName,
          lastName: e.lastName,
          department: e.department,
        })),
        breakTypes: breakTypes.map((bt) => ({
          id: bt.id,
          name: bt.name,
          isPaid: bt.isPaid,
        })),
        summary: {
          totalEvents: events.length,
          pendingEvents: events.filter((e) => e.syncStatus === 'pending').length,
          syncedEvents: events.filter((e) => e.syncStatus === 'synced').length,
          employeeCount: employees.length,
        },
      };

      const jsonStr = JSON.stringify(exportData, null, 2);
      const safeSite = (deviceInfo?.location_name || 'kiosk').replace(/[^a-zA-Z0-9-_]/g, '_');
      downloadFile(
        jsonStr,
        `kiosk_data_${safeSite}_${startDate}_to_${endDate}.json`,
        'application/json'
      );

      return { success: true, eventCount: events.length };
    } catch (err) {
      console.error('JSON export failed:', err);
      return { success: false, error: err.message };
    } finally {
      setExporting(false);
    }
  }, []);

  // -----------------------------------------------------------------------
  // Generate QR code data chunks — for phone-based transfer
  // -----------------------------------------------------------------------
  const generateQRCodes = useCallback(async (startDate, endDate) => {
    try {
      const [events, employees, deviceInfo] = await Promise.all([
        dbOps.getEventsByDateRange(startDate, endDate + 'T23:59:59'),
        dbOps.getAllEmployees(),
        dbOps.getConfig('device_info'),
      ]);

      // Build compact event data (no selfies, minimal fields)
      const compactEvents = events.map((e) => ({
        t: e.tempId,
        u: e.userId,
        a: e.action,
        ts: e.timestamp,
        m: e.method || undefined,
        b: e.breakTypeId || undefined,
        s: e.syncStatus === 'synced' ? 1 : 0,
      }));

      // Employee lookup (only include referenced employees)
      const referencedIds = new Set(events.map((e) => e.userId));
      const empLookup = {};
      for (const emp of employees) {
        if (referencedIds.has(emp.id)) {
          empLookup[emp.id] = emp.displayName;
        }
      }

      const payload = {
        v: 1,
        d: deviceInfo?.id || null,
        s: deviceInfo?.site_location_id || null,
        c: deviceInfo?.company_id || null,
        r: { s: startDate, e: endDate },
        emp: empLookup,
        evt: compactEvents,
      };

      const jsonStr = JSON.stringify(payload);

      // Split into QR-safe chunks (~1800 bytes each for reliable scanning)
      const CHUNK_SIZE = 1800;
      const chunks = [];
      for (let i = 0; i < jsonStr.length; i += CHUNK_SIZE) {
        chunks.push(jsonStr.slice(i, i + CHUNK_SIZE));
      }

      // Wrap each chunk with metadata for reassembly
      const wrappedChunks = chunks.map((chunk, i) => {
        return JSON.stringify({
          _qr: true,
          p: i + 1,
          t: chunks.length,
          d: chunk,
        });
      });

      const result = {
        chunks: wrappedChunks,
        totalChunks: wrappedChunks.length,
        totalEvents: events.length,
        rawSize: jsonStr.length,
      };

      setQrData(result);
      return result;
    } catch (err) {
      console.error('QR generation failed:', err);
      setQrData(null);
      return { success: false, error: err.message };
    }
  }, []);

  // -----------------------------------------------------------------------
  // Clear QR data
  // -----------------------------------------------------------------------
  const clearQRData = useCallback(() => {
    setQrData(null);
  }, []);

  return {
    exporting,
    qrData,
    exportToCSV,
    exportToJSON,
    generateQRCodes,
    clearQRData,
  };
}
